import { JWT, LANG, SPTENANTID, SPUSERGUID } from "@/configs";
import { hasPendingWorkspaceSelection } from "@/lib/auth/sessionUser";
import { normalizeAvatarFields } from "@/slices/auth/authAvatar";
import { login as loginAction } from "@/slices/auth/authReducer";
import { store } from "@/store";
import { decode } from "@/utils/jwt";
import cookie from "js-cookie";

export const setCookie = (key, value, expireDay = 365, serverSideReq?, serverSideRes?) => {
  const options = {
    expires: expireDay,
    path: "/"
  };

  if (typeof window !== "undefined") {
    cookie.set(key, value, options);
  } else if (serverSideReq && serverSideRes) {
    serverSideRes.setHeader(
      "Set-Cookie",
      `${key}=${value}; Expires=${new Date(new Date().getTime() + expireDay * 24 * 60 * 60 * 1000).toUTCString()}; Path=/`
    );
  }
};

export const removeCookie = (key, serverSideReq?, serverSideRes?) => {
  if (typeof window !== "undefined") {
    cookie.remove(key, { path: "/" });
  } else {
    if (serverSideReq && serverSideRes) {
      serverSideRes.setHeader("Set-Cookie", `${key}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`);
    }
  }
};

export const removeAuthorizedUser = (serverSideReq?, serverSideRes?) => {
  removeCookie(JWT, serverSideReq, serverSideRes);
};

export const addAuthorizedUser = (token, serverSideReq?, serverSideRes?) => {
  setCookie(JWT, token, 365, serverSideReq, serverSideRes);
};

export const getAuthorizedUser = (serverSideReq?) => {
  return getCookie(JWT, serverSideReq) || "";
};

export const changeLocale = (langKey, serverSideReq?, serverSideRes?) => {
  setCookie(LANG, langKey, 365, serverSideReq, serverSideRes);
};

export const getCookie = (key, serverSideReq?) => {
  return typeof window !== "undefined"
    ? getCookieFromBrowser(key)
    : serverSideReq
      ? getCookieFromServer(key, serverSideReq)
      : undefined;
};

export const getCookieFromResponse = (key, res?) => {
  if (!res.headers.cookie) {
    return undefined;
  }
  const rawCookie = res.headers.cookie.split(";").find((c) => c.trim().startsWith(`${key}=`));
  if (!rawCookie) {
    return undefined;
  }
  return rawCookie.split("=")[1];
};
// parse cookieString and return json object with key, value, and options
export const parseCookie = (cookieString: string) => {
  const [keyValue, ...options] = cookieString.split(";");
  const [key, value] = keyValue.split("=");

  const parsedCookie = {
    key: key.trim(),
    value: value.trim(),
    options: options.reduce((acc, option) => {
      const [optionKey, optionValue] = option.split("=");
      return {
        ...acc,
        [optionKey.trim()]: optionValue.trim()
      };
    }, {})
  };

  return parsedCookie;
};

export const getSetCookieFromResponse = (res?) => {
  if (!res.headers.cookie) {
    return undefined;
  }
  const rawCookies = res.headers.cookie.split(";").map((c) => {
    const ck = parseCookie(c);
    if (typeof window !== "undefined") {
      cookie.set(ck.key, ck.value, ck.options);
    }
    return ck;
  });
  if (!rawCookies) {
    return undefined;
  }
  return rawCookies;
};

const getCookieFromBrowser = (key) => {
  return cookie.get(key);
};

const getCookieFromServer = (key, req) => {
  if (!req.headers.cookie) {
    return undefined;
  }
  const rawCookie = req.headers.cookie.split(";").find((c) => c.trim().startsWith(`${key}=`));
  if (!rawCookie) {
    return undefined;
  }
  return rawCookie.split("=")[1];
};

export const setLoginResponse = (login, tenantId, serverSideReq?, serverSideRes?) => {
  const status = login.content.status;
  if (status === "OK") {
    const stAccessToken = login.headers["st-access-token"];
    const user = login.content.user;
    const decoded = decode(stAccessToken);
    // supertokens does not support additional values,  so use jwt to return additional values
    if (user.email && user.email.endsWith("firstTimeLogin")) {
      user.firstLogin = true;
    }
    user.spUserGuid = decoded.spUGuid;
    user.stUserGuid = decoded.sub;
    user.id = decoded.spUGuid;
    user.email = decoded.spEmail;
    user.role = decoded.role; // 0: admin, 1: user user.role
    user.groups = decoded.groups;
    user.tenantName = decoded.spTid;
    user.tenantGuid = decoded.spTGuid;
    user.tenantKind = decoded.spTKind;
    user.spAId = decoded.spAId || user.spAId;
    user.accountGuid = decoded.spAId || user.accountGuid;
    user.pendingWorkspaceSelection = hasPendingWorkspaceSelection({
      pendingWorkspaceSelection: decoded.spPendingWorkspaceSelection,
      spPendingWorkspaceSelection: decoded.spPendingWorkspaceSelection,
      tenantKind: decoded.spTKind
    });
    const authenticatedUser = normalizeAvatarFields(user);
    setCookie(SPTENANTID, tenantId, 365, serverSideReq, serverSideRes);
    setCookie(SPUSERGUID, authenticatedUser.spUserGuid, 365, serverSideReq, serverSideRes);
    store.dispatch(loginAction({ user: authenticatedUser, role: authenticatedUser.role }));
  }
};
